import * as React from "react";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";
import calendarPlugin from "dayjs/plugin/calendar";

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(calendarPlugin);

import ApiService from "@/api/ApiService";

import {
  AlarmClock,
  AlertTriangle,
  ArrowRightLeft,
  BadgeCheck,
  BadgeX,
  Briefcase,
  CalendarCheck2,
  CalendarClock,
  CalendarDays,
  CalendarX2,
  ClipboardCheck,
  ClipboardList,
  ClipboardX,
  CloudLightning,
  Construction,
  FolderCheck,
  Hammer,
  HardHat,
  Info,
  MapPin,
  MessageCircle,
  MessageSquare,
  Package,
  ShoppingCart,
  Stethoscope,
  Truck,
  Wrench,
} from "lucide-react";
import StyledModal from "@/components/planning/dialogs/newMaterialOrder/StyledModal";
import { useNavigate } from "react-router-dom";
import NotificationSettings from "./NotificationSettings";

interface NotificationFullTextItem {
  type: "text" | "lighttext" | "link" | "externallink";
  content: string;
  to?: string;
  emailTo?: string;
}

interface NotificationItem {
  id: number;
  recipientId: number;
  createdAt: string; // "YYYY-MM-DD HH:mm:ss"
  title: string;
  subtitle: string;
  status: "OPEN" | "READ";
  icon?: string;
  fulltext: NotificationFullTextItem[];
}

interface Section {
  title: string;
  date: string; // YYYY-MM-DD
  data: NotificationItem[];
}

const calendar = {
  sameDay: "[Heute]",
  nextDay: "[Morgen]",
  nextWeek: "ddd, D. MMMM YYYY",
  lastDay: "[Gestern]",
  lastWeek: "ddd, D. MMMM YYYY",
  sameElse: "ddd, D. MMMM YYYY",
};

type IconName = NotificationItem["icon"];

function RenderIcon({ name }: { name?: IconName }) {
  switch (name) {
    case "CloudLightning":
      return <CloudLightning />;
    case "Info":
      return <Info />;
    case "Warning":
    case "AlertTriangle":
      return <AlertTriangle />;
    case "CalendarSync":
    case "CalendarClock":
      return <CalendarClock />;
    case "CalendarDays":
      return <CalendarDays />;
    case "AlarmClock":
      return <AlarmClock />;
    case "Message":
      return <MessageSquare />;
    case "MessageCircle":
      return <MessageCircle />;
    case "ClipboardList":
      return <ClipboardList />;
    case "ClipboardCheck":
      return <ClipboardCheck />;
    case "ClipboardX":
      return <ClipboardX />;
    case "BadgeCheck":
      return <BadgeCheck />;
    case "BadgeX":
      return <BadgeX />;
    case "ShoppingCart":
      return <ShoppingCart />;
    case "Package":
      return <Package />;
    case "Truck":
      return <Truck />;
    case "Briefcase":
      return <Briefcase />;
    case "Construction":
      return <Construction />;
    case "HardHat":
      return <HardHat />;
    case "Hammer":
      return <Hammer />;
    case "Wrench":
      return <Wrench />;
    case "FolderCheck":
      return <FolderCheck />;
    case "Stethoscope":
      return <Stethoscope />;
    case "ArrowRightLeft":
      return <ArrowRightLeft />;
    case "MapPin":
      return <MapPin />;
    case "CalendarCheck2":
      return <CalendarCheck2 />;
    case "CalendarX2":
      return <CalendarX2 />;
    default:
      return <MessageCircle />;
  }
}

export function NotificationButtons() {
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <NotificationReadAllButton />
      <NotificationSettings />
    </Box>
  );
}

export function NotificationReadAllButton() {
  const queryClient = useQueryClient();

  const readMutation = useMutation<unknown, Error, { id: number | "ALL" }>({
    mutationFn: ApiService.setNotificationStatus(), // keep your service signature
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getNotification"] });
    },
  });
  return (
    <IconButton
      onClick={() => readMutation.mutate({ id: "ALL" })}
      disabled={readMutation.isPending}
      size="small"
      sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
      aria-label="Alle als gelesen markieren"
    >
      <MarkEmailReadIcon fontSize="inherit" />
    </IconButton>
  );
}

export default function Notifications() {
  const queryClient = useQueryClient();
  const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null);
  const navigate = useNavigate();
  const query = useQuery<NotificationItem[], Error>({
    queryKey: ["getNotification"],
    queryFn: ApiService.getNotifications(), // keep your service signature
    staleTime: 30_000,
  });

  const readMutation = useMutation<unknown, Error, { id: number | "ALL" }>({
    mutationFn: ApiService.setNotificationStatus(), // keep your service signature
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getNotification"] });
    },
  });

  const DATA = useMemo<Section[]>(() => {
    if (!query.isSuccess || !Array.isArray(query.data)) return [];

    const buckets: Record<string, Section> = {};

    for (const n of query.data) {
      const date = dayjs.utc(n.createdAt, "YYYY-MM-DD HH:mm:ss").local();
      const title = date.calendar(undefined, calendar as any);
      if (!buckets[title]) {
        buckets[title] = { title, date: date.format("YYYY-MM-DD"), data: [] };
      }
      buckets[title].data.push(n);
    }

    // optional: sort items in each bucket newest first
    for (const k of Object.keys(buckets)) {
      buckets[k].data.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }

    return Object.values(buckets).sort((a, b) => b.date.localeCompare(a.date));
  }, [query.isSuccess, query.data]);

  return (
    <Box sx={{ display: "flex", flex: 1, position: "relative", overflow: "auto", flexDirection: "column" }}>
      {/* header */}
      {/* states */}
      {query.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4, alignSelf: "center", color: "#444", flex: 1 }}>
          <CircularProgress size={24} />
        </Box>
      ) : query.isError ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, alignSelf: "center", color: "#444", flex: 1 }}>
          <Typography color="error">Fehler beim Laden der Benachrichtigungen.</Typography>
        </Box>
      ) : !query.data || query.data.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, alignSelf: "center", color: "#444", flex: 1 }}>
          <Typography>Nichts gefunden</Typography>
        </Box>
      ) : (
        <Box sx={{}}>
          {DATA.map((section) => (
            <React.Fragment key={`${section.title}-${section.date}`} sx={{}}>
              <div className="notificationDate">{section.title}</div>

              {section.data.map((data) => (
                <div
                  key={String(data.recipientId)}
                  onClick={() => {
                    setSelectedNotification(data);
                    // mark this specific notification as read
                    if (data.status === "OPEN") readMutation.mutate({ id: data.recipientId });
                  }}
                  className={"overduebox notificationbox"}
                >
                  <div className={"obcolor color " + (data.status === "OPEN" ? "blue" : "lightgrey")}>
                    <RenderIcon name={data.icon} />
                  </div>
                  <div className="obfacts">
                    <div className="obpro">{data.title}</div>
                    {data.subtitle?.length ? <div className="obbl">{data.subtitle}</div> : null}
                    <div className="obdateline">
                      <div className="obdate">{dayjs.utc(data.createdAt, "YYYY-MM-DD HH:mm:ss").local().fromNow()}</div>
                    </div>
                  </div>
                </div>
              ))}
            </React.Fragment>
          ))}
        </Box>
      )}
      <StyledModal open={!!selectedNotification} onShow={() => {}} onClose={() => setSelectedNotification(null)}>
        {!!selectedNotification ? (
          <Box sx={{ display: "flex", gap: "10px", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "4px",
                borderBottom: "1px solid #aaa",
                backgroundColor: "#eee",
                p: "10px",
              }}
            >
              <RenderIcon name={selectedNotification.icon} />
              <Box sx={{ display: "flex", flexDirection: "column" }} gap="$1" flex={1}>
                <Box sx={{ fontWeight: "bold" }}>{selectedNotification.title}</Box>
                <Box>{selectedNotification.subtitle}</Box>
              </Box>
              <Box sx={{ paddingX: "7px", color: "#555" }} px="$3" color="$color10">
                {dayjs.utc(selectedNotification.createdAt, "YYYY-MM-DD HH:mm:ss").local().fromNow()}
              </Box>{" "}
            </Box>

            {selectedNotification.fulltext.map((f, i) => {
              if (f.type === "text") return <Box key={i}>{f.content}</Box>;
              if (f.type === "lighttext")
                return (
                  <Box key={i} sx={{ color: "#666" }}>
                    {f.content}
                  </Box>
                );
              if (f.type === "link" || f.type === "externallink") {
                return (
                  <Button
                    key={i}
                    onClick={() => {
                      const target = f.type === "externallink" ? f.to : f.emailTo || f.to;
                      if (target) {
                        if (f.type === "externallink" || /^https?:\/\//i.test(target)) {
                          window.open(target, "_blank", "noopener,noreferrer");
                        } else {
                          navigate(target);
                        }
                      }

                      setSelectedNotification(null);
                    }}
                  >
                    {f.content}
                  </Button>
                );
              }
              return null;
            })}
            {selectedNotification.fulltext.length === 0 ? <Box sx={{ color: "#666", textAlign: "center" }}>Keine weiteren Details</Box> : null}
          </Box>
        ) : null}
      </StyledModal>
    </Box>
  );
}
